import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { ChevronDown, Check, Building2, Plane, Heart } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

// Brand icons mapping
const brandIcons = {
  "MAAVI": Building2,
  "TripMate AI": Plane,
  "Solo&Co": Heart
};

// Brand colors mapping
const brandColors = {
  "MAAVI": "bg-blue-500",
  "TripMate AI": "bg-emerald-500",
  "Solo&Co": "bg-pink-500"
};

const BrandSelector = ({ onBrandChange }) => {
  const [brands, setBrands] = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
    fetchActiveBrand();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveBrand = async () => {
    try {
      const response = await axios.get(`${API}/brands/active`);
      setActiveBrand(response.data);
      if (onBrandChange) {
        onBrandChange(response.data);
      }
    } catch (error) {
      console.error("Error fetching active brand:", error);
    }
  };

  const handleSelectBrand = async (brand) => {
    try {
      await axios.put(`${API}/brands/active/${brand.id}`);
      setActiveBrand(brand);
      if (onBrandChange) {
        onBrandChange(brand);
      }
      toast.success(`Marque active: ${brand.name}`);
    } catch (error) {
      toast.error("Erreur lors du changement de marque");
    }
  };

  if (loading || !activeBrand) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 animate-pulse">
        <div className="w-8 h-8 rounded-full bg-muted" />
        <div className="flex-1">
          <div className="h-4 w-20 bg-muted rounded" />
          <div className="h-3 w-16 bg-muted rounded mt-1" />
        </div>
      </div>
    );
  }

  const ActiveIcon = brandIcons[activeBrand.name] || Building2;
  const activeColor = brandColors[activeBrand.name] || "bg-primary";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="w-full justify-start gap-3 p-3 h-auto rounded-lg bg-muted/30 hover:bg-muted/50"
          data-testid="brand-selector"
        >
          <div className={`w-8 h-8 rounded-full ${activeColor} flex items-center justify-center`}>
            <ActiveIcon className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-medium truncate">{activeBrand.name}</p>
            <p className="text-xs text-muted-foreground">Marque active</p>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Changer de marque</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {brands.map((brand) => {
          const Icon = brandIcons[brand.name] || Building2;
          const color = brandColors[brand.name] || "bg-primary";
          const isActive = activeBrand.id === brand.id;
          
          return (
            <DropdownMenuItem
              key={brand.id}
              onClick={() => handleSelectBrand(brand)}
              className="cursor-pointer"
              data-testid={`brand-option-${brand.id}`}
            >
              <div className={`w-6 h-6 rounded-full ${color} flex items-center justify-center mr-3`}>
                <Icon className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{brand.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {brand.target_audience?.substring(0, 30)}...
                </p>
              </div>
              {isActive && <Check className="w-4 h-4 text-primary ml-2" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BrandSelector;
